import { map, shareReplay } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { User } from './user.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ROLE_TYPE } from './role-type';
import { environment } from '@environments/environment';

import { BaseRestApiService } from '@shared/services/base-rest-api.service';
import { RestApiWrapperService } from '@shared/services/rest-api-wrapper.service';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseRestApiService {
  userUri = environment.serverUri + `api/${environment.apiVersion}/users`;
  private _currentUser: User;

  private _userRole = new BehaviorSubject<ROLE_TYPE>(null);
  userRole$ = this._userRole.asObservable();

  private getUser$ = this.get(`${this.userUri}/0`).pipe(shareReplay(1));

  constructor(_restApiWrapperService: RestApiWrapperService) {
    super(_restApiWrapperService);
  }

  userAvailable() {
    return this._currentUser === undefined ? false : true;
  }

  // Note: When we switch organization to keycloak, we can get user information from access_token which has group and role
  getCurrentUser(): Observable<User> {
    if (this._currentUser) {
      return of(this._currentUser);
    } else {
      return this.getUser$.pipe(
        map(response => {
          const user = new User(response);
          this._currentUser = user;
          return user;
        })
      );
    }
  }

  getUsersByOrganizationId(organizationId: string): Observable<User[]> {
    return this.get<User[]>(`${this.userUri}/organizations/${organizationId}`);
  }

  createUpdate(user: User): Observable<User> {
    if (user.id) {
      return this.put<User>(`${this.userUri}/${user.id}`, JSON.stringify(user));
    }
    return this.post<User>(this.userUri, JSON.stringify(user));
  }

  /**
   * TODO: it won't work sometime, especially when a user reloads a page.
   */
  get organizationId(): string {
    if (this._currentUser) {
      return this._currentUser.organization.id;
    }
  }

  get isCreditOnlyUser(): boolean {
    const creditOnlyUser = this.hasRole(ROLE_TYPE.DEVELOPER_CREDIT) &&
      !this.hasRole(ROLE_TYPE.DEVELOPER_USER) &&
      !this.hasRole(ROLE_TYPE.FINANCE_USER) &&
      !this.hasRole(ROLE_TYPE.DATA_ANALYST) &&
      !this.hasRole(ROLE_TYPE.VENDOR_USER);

    const { credit, developer } = this._currentUser.organization;
    const isCreditOrDeveloperOrg = credit || developer;

    return creditOnlyUser && isCreditOrDeveloperOrg;
  }

  get zohoAccountId(): string {
    if (!this._currentUser) {
      return '';
    }

    return this._currentUser.organization.zohoAccountId;
  }

  get isDeveloperAndCreditUser(): boolean {
    return this.hasRole(ROLE_TYPE.DEVELOPER_USER) &&
      this.hasRole(ROLE_TYPE.DEVELOPER_CREDIT);
  }

  isSystemAdmin(): boolean {
    return this.hasRole(ROLE_TYPE.SYSTEM_ADMIN);
  }

  isProgramAdmin(): boolean {
    return this.hasRole(ROLE_TYPE.FINANCE_USER);
  }

  get passwordChanged(): boolean {
    if (this._currentUser) {
      return this._currentUser.passwordChanged ? true : false;
    }
    return false;
  }

  hasRole(roleType: ROLE_TYPE): boolean {
    if (this._currentUser?.roles?.length > 0) {
      return this._currentUser.roles.some(role => role.name === roleType);
    }
    return false;
  }

  setSelectedRole(role: ROLE_TYPE) {
    this._currentUser.selectedRole = role;
    localStorage.setItem('role_type', ROLE_TYPE[role]);
    this._userRole.next(ROLE_TYPE[role]);
  }

  // only care about developer, investor and vendor roles.
  getSelectedRole(): ROLE_TYPE {
    if (!this._currentUser) {
      return undefined;
    }

    let initialRoleType;
    // use saved role
    const savedRole = localStorage.getItem('role_type');
    // compare with text undefined
    if (savedRole && savedRole !== 'undefined' &&
      savedRole !== ROLE_TYPE.DATA_ANALYST &&
      savedRole !== ROLE_TYPE.ORGANIZATION_ADMIN &&
      savedRole !== ROLE_TYPE.SYSTEM_ADMIN) {
      // check developer, investor and vendor
      if (this.hasRole(ROLE_TYPE[savedRole])) {
        initialRoleType = ROLE_TYPE[savedRole];
      } else {
        initialRoleType = this.takeFirstRole();
      }
    } else {
      // no saved role, use dev, investor or vendor.
      if (this._currentUser.roles.length > 0) {
        initialRoleType = this.takeFirstRole();
      }
    }
    if (!this._currentUser.selectedRole || this._currentUser.selectedRole !== initialRoleType) {
      this._currentUser.selectedRole = initialRoleType;
      this._userRole.next(initialRoleType);
    }
    return this._currentUser.selectedRole;
  }

  private takeFirstRole(): ROLE_TYPE {
    if (this.hasRole(ROLE_TYPE.DEVELOPER_USER)) {
      return ROLE_TYPE.DEVELOPER_USER;
    } else if (this.hasRole(ROLE_TYPE.FINANCE_USER)) {
      return ROLE_TYPE.FINANCE_USER;
    } else if (this.hasRole(ROLE_TYPE.VENDOR_USER)) {
      return ROLE_TYPE.VENDOR_USER;
    } else if (this.hasRole(ROLE_TYPE.DEVELOPER_CREDIT)) {
      return ROLE_TYPE.DEVELOPER_CREDIT;
    }
  }

  clear() {
    this._currentUser = undefined;
    const savedRole = localStorage.getItem('role_type');
    if (savedRole === ROLE_TYPE.SYSTEM_ADMIN) {
      localStorage.removeItem('role_type');
    }
    this.getUser$ = this.get(`${this.userUri}/0`).pipe(shareReplay(1));
  }
}
